/**
 * subjectSectors — canonical subject → City Matcher sector hint.
 *
 * Feeds the cross-pillar handoff from Course Fit into City Matcher.
 * When Course Fit resolves a `canonicalSubject`, we map it to one (or
 * more) of the 9 broad sector chips the Matcher exposes:
 *   tech · finance · law · creative · healthcare
 *   engineering · startups · government · research
 *
 * Keep this mapping conservative — false positives dilute the filter,
 * false negatives just fall back to "no hint" (student picks manually).
 */

// Ordered by rough coverage. Each rule is a case-insensitive substring
// match against either the canonical subject slug or its display label.
const RULES = [
  // Healthcare
  { needles: ["medicine", "medical", "nursing", "dentistry", "dental", "pharmacy",
              "veterinary", "midwifery", "physiotherapy", "biomedical", "public_health",
              "public health", "healthcare", "health_science", "health science",
              "optometry", "paramedic", "radiography", "occupational_therapy"], sector: "healthcare" },
  // Law
  { needles: ["law", "legal_studies", "legal studies", "jurisprudence", "criminology"], sector: "law" },
  // Tech
  { needles: ["computer_science", "computer science", "computing", "informatics",
              "software", "artificial_intelligence", "ai_", "data_science", "data science",
              "cyber", "machine_learning", "machine learning"], sector: "tech" },
  // Finance
  { needles: ["finance", "accounting", "banking", "actuarial", "economics",
              "econometrics", "investment"], sector: "finance" },
  // Engineering
  { needles: ["engineering", "aerospace", "mechatronic", "robotics"], sector: "engineering" },
  // Creative & Media
  { needles: ["film", "media", "journalism", "communications", "creative",
              "design", "fine_art", "fine art", "art_history", "architecture",
              "music", "drama", "theatre", "performance", "fashion", "animation",
              "photography", "publishing", "games", "gaming"], sector: "creative" },
  // Government / Policy
  { needles: ["politics", "government", "public_policy", "public policy",
              "international_relations", "international relations", "diplomacy",
              "peace_studies"], sector: "government" },
  // Research-flavoured pure sciences & humanities
  { needles: ["physics", "chemistry", "biology", "biochemistry", "mathematics",
              "philosophy", "linguistics", "geology", "geography", "anthropology",
              "sociology", "history", "psychology", "astronomy"], sector: "research" },
];

export function sectorForSubject(subjectSlug, subjectLabel = "") {
  const hay = `${(subjectSlug || "").toLowerCase()} ${(subjectLabel || "").toLowerCase()}`;
  for (const { needles, sector } of RULES) {
    if (needles.some((n) => hay.includes(n))) return sector;
  }
  return null;
}

// Human-readable labels for the sector chips (matches CityMatcher).
export const SECTOR_LABELS = {
  tech: "Software & AI",
  finance: "Finance",
  law: "Law",
  creative: "Creative & Media",
  healthcare: "Healthcare",
  engineering: "Engineering",
  startups: "Startups",
  government: "Government / Policy",
  research: "Research",
};
